import React from 'react';
import '../../styles/ClaimFormsDragAndDrop.css';
import '../../styles/ReinsUpdater.css';
import {read, utils} from 'xlsx';
import axios from 'axios';
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {GlobalConstants} from "../../globals/GlobalConstants";
import {refreshToken} from "../../globals/GlobalFunctions";
import {KEY_COLUMNS, FILE_KINDS} from "./ReinsContractFiles";

//what the backend sends back for one contract
type ImportResult = {
    fileName: string;
    contract_ID: string | null;
    success: boolean;
    message: string | null;
    reinsContract_Key: number | null;
    //true when an existing contract was overwritten
    updated: boolean;
    errors: string[];
};

//true for real workbooks, skips excel's hidden ~$ lock files
const isWorkbook = (file: File) => /\.xlsx$/i.test(file.name) && !file.name.startsWith('~$');

//reads every file inside a dropped folder
const readFolder = async (entry: any): Promise<File[]> => {
    if (entry.isFile) {
        return [await new Promise<File>((resolve, reject) => entry.file(resolve, reject))];
    }

    //the browser hands back folder contents in batches so keep reading until a batch is empty
    const reader = entry.createReader();
    const files: File[] = [];
    while (true) {
        const batch: any[] = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        if (batch.length === 0) break;
        for (const child of batch) files.push(...await readFolder(child));
    }
    return files;
};

//reads the first tab of a workbook
//each row is {column header: cell text, Row: excel row number}, blank cells and rows left out
const readFirstTab = async (file: File) => {
    const wb: any = read(await file.arrayBuffer(), {cellDates: true});
    const sheet = wb.Sheets[wb.SheetNames[0]];

    const headerRow: any[] = (utils.sheet_to_json(sheet, {header: 1}) as any[][])[0] ?? [];
    const headers = headerRow.map(header => String(header ?? '').trim());

    //dates come back as they show in excel (mm/dd/yyyy), numbers come back plain with no commas
    const rows: any[] = utils.sheet_to_json(sheet, {raw: false, rawNumbers: true, defval: ''});
    const cleanRows = rows
        .map(row => {
            const clean: any = {};
            for (const header in row) {
                const value = String(row[header]).trim();
                if (value !== '') clean[header.trim()] = value;
            }
            //sheet_to_json keeps the 0 based sheet row in __rowNum__
            clean.Row = row.__rowNum__ + 1;
            return clean;
        })
        .filter(row => Object.keys(row).length > 1);

    return {headers, rows: cleanRows};
};

//same date typed two ways (2/1/2025, 02/01/2025) has to give the same key
const sameDate = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value.toUpperCase();
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

//Contract ID + Eff. Date + Division ID, the link between the 4 files
const contractKey = (row: any) =>
    [String(row['Contract ID'] ?? '').toUpperCase(), sameDate(String(row['Eff. Date'] ?? '')), String(row['Division ID'] ?? '').toUpperCase()].join('|');

const contractName = (row: any) =>
    `${row['Contract ID'] ?? '(no Contract ID)'} ${sameDate(String(row['Eff. Date'] ?? ''))}` + (row['Division ID'] ? ` division ${row['Division ID']}` : '');

//reads the dropped files and splits them into one request body per contract
//problems with the files themselves (missing file, rows with no contract) come back in problems
const splitIntoContracts = async (files: File[]) => {
    const problems: string[] = [];
    const tabs: any = {};

    for (const file of files) {
        const {headers, rows} = await readFirstTab(file);
        const kind = FILE_KINDS.find(k => k.headers.every(header => headers.includes(header)));

        if (!kind) {
            problems.push(`${file.name}: the column headers don't match any of the 4 import files.`);
            continue;
        }
        if (tabs[kind.name]) {
            problems.push(`${file.name}: there is already a ${kind.name} file (${tabs[kind.name].fileName}). Drop only one of each.`);
            continue;
        }
        const missingColumns = kind.columns.filter(column => !headers.includes(column));
        if (missingColumns.length > 0) {
            problems.push(`${file.name}: missing the column${missingColumns.length === 1 ? '' : 's'} ${missingColumns.join(', ')}.`);
        }
        tabs[kind.name] = {fileName: file.name, rows};
    }

    for (const kind of FILE_KINDS) {
        if (!tabs[kind.name]) problems.push(`The ${kind.name} file is missing.`);
    }
    if (problems.length > 0) return {contracts: [], problems};

    //one request body per Contract Info row
    const contracts: any = {};
    //which Contract Info row each contract came from, for the "listed twice" message
    const infoRows: any = {};
    for (const row of tabs['Contract Info'].rows) {
        const key = contractKey(row);
        if (contracts[key]) {
            problems.push(`Contract Info row ${row.Row}: ${contractName(row)} is listed twice (also row ${infoRows[key]}).`);
            continue;
        }

        //every column becomes a Field / Value pair
        const fields = [];
        for (const field in row) {
            if (field !== 'Row') fields.push({Field: field, Value: row[field]});
        }

        infoRows[key] = row.Row;
        contracts[key] = {
            fileName: contractName(row),
            Contract: fields,
            'Contract Criteria': [],
            'Contract Factors': [],
            'Contract Lasers': []
        };
    }

    //every other row goes to the contract with the same key
    for (const kind of ['Contract Criteria', 'Contract Factors', 'Contract Lasers']) {
        for (const row of tabs[kind].rows) {
            const contract = contracts[contractKey(row)];
            if (!contract) {
                problems.push(`${kind} row ${row.Row}: no contract in Contract Info matches ${contractName(row)}.`);
                continue;
            }

            //the key columns were only needed to match the row up
            const rest = {...row};
            for (const column of KEY_COLUMNS) delete rest[column];
            contract[kind].push(rest);
        }
    }

    return {contracts: Object.values(contracts), problems};
};

function ReinsContractImporter() {
    //State is used here to manipulate what is displayed by the UI while dragging a file and what renders in response to the drop
    const [dragging, setDragging] = React.useState(false);
    const [files, setFiles] = React.useState<File[]>([]);
    //one request body per contract, built from the dropped files
    const [contracts, setContracts] = React.useState<any[]>([]);
    //problems with the files themselves (missing file, rows that match no contract)
    const [fileProblems, setFileProblems] = React.useState<string[]>([]);
    const [currentDB, setCurrentDB] = React.useState("CoastalAdminTest");
    //ticked = a contract that already exists is overwritten, unticked = it is an error
    const [updateExisting, setUpdateExisting] = React.useState(false);
    const [importing, setImporting] = React.useState(false);
    const [progress, setProgress] = React.useState('');
    const [results, setResults] = React.useState<ImportResult[]>([]);
    const [checkFailed, setCheckFailed] = React.useState(false);
    const [error, setError] = React.useState<string>('');

    const onDBChange = React.useCallback((e: any, newDB: any) => setCurrentDB(newDB.id), []);

    //Values used for detecting hover state and applying effects as needed
    const drop = React.useRef<any>(null);
    const drag = React.useRef(null);

    React.useEffect(() => {

        //Stops default when dragging file
        const handleDragOver = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
        };

        //When dropping files or folders this collects every workbook in them
        const handleDrop = async (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(false);
            setResults([]);
            setCheckFailed(false);
            setError('');
            setFiles([]);
            setContracts([]);
            setFileProblems([]);

            //entries have to be grabbed right away the browser clears them after the first await
            const entries = Array.from(e.dataTransfer.items).map((item: any) => item.webkitGetAsEntry());

            try {
                let dropped: File[] = [];
                for (const entry of entries) {
                    if (entry) dropped.push(...await readFolder(entry));
                }

                dropped = dropped.filter(isWorkbook);
                if (dropped.length === 0) {
                    setError('No .xlsx files found. Drop the Contract Info, Contract Criteria, Contract Factors and Contract Lasers files.');
                    return;
                }

                setFiles(dropped);

                const split = await splitIntoContracts(dropped);
                setContracts(split.contracts);
                setFileProblems(split.problems);
            } catch (err: any) {
                setError('Those files could not be read. ' + (err?.message ?? ''));
            }
        };

        //changes UI when dragging file over
        const handleDragEnter = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(true);
        };

        //opposite of above, resets when dragging away from drop area
        const handleDragLeave = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            if (e.target === drag.current) {
                setDragging(false);
            }
        };

        //setting event listeners
        drop?.current?.addEventListener('dragover', handleDragOver);
        drop?.current?.addEventListener('drop', handleDrop);
        drop?.current?.addEventListener('dragenter', handleDragEnter);
        drop?.current?.addEventListener('dragleave', handleDragLeave);

        return () => {
            drop?.current?.removeEventListener('dragover', handleDragOver);
            drop?.current?.removeEventListener('drop', handleDrop);
            drop?.current?.removeEventListener('dragenter', handleDragEnter);
            //eslint-disable-next-line react-hooks/exhaustive-deps
            drop?.current?.removeEventListener('dragleave', handleDragLeave);
        };
    }, []);

    //sends one contract to the backend, checkOnly checks it and writes nothing
    const send = async (contract: any, checkOnly: boolean): Promise<ImportResult> => {
        try {
            const body = {
                ...contract,
                vbasoftware_database: currentDB,
                checkOnly: checkOnly,
                updateExisting: updateExisting
            };

            const response = await axios.post(
                localStorage.getItem('baseURL') + 'reinscontract/import',
                body,
                {
                    headers: {Authorization: 'Bearer ' + localStorage.getItem('jwt')},
                    //15 minutes
                    timeout: 15 * 60 * 1000
                }
            );
            return response.data;
        } catch (err: any) {
            //a failed check comes back as a 400 with the same result shape
            if (err?.response?.data?.errors) return err.response.data;

            return {
                fileName: contract.fileName,
                contract_ID: null,
                success: false,
                message: 'The request failed.',
                reinsContract_Key: null,
                updated: false,
                errors: [err?.message ?? 'Unknown error']
            };
        }
    };

    //checks every contract first, and only imports if all of them pass
    const importAll = async () => {
        if (contracts.length === 0 && fileProblems.length === 0) return;

        refreshToken();
        setImporting(true);
        setError('');
        setResults([]);
        setCheckFailed(false);

        const checked: ImportResult[] = [];
        for (let i = 0; i < contracts.length; i++) {
            setProgress(`Checking ${i + 1} of ${contracts.length}...`);
            checked.push(await send(contracts[i], true));
        }

        //problems with the files go first in the error log, and also stop the import
        if (fileProblems.length > 0) {
            checked.unshift({
                fileName: 'Dropped files',
                contract_ID: null,
                success: false,
                message: null,
                reinsContract_Key: null,
                updated: false,
                errors: fileProblems
            });
        }

        if (checked.some(result => !result.success)) {
            setResults(checked);
            setCheckFailed(true);
            setImporting(false);
            return;
        }

        const imported: ImportResult[] = [];
        for (let i = 0; i < contracts.length; i++) {
            setProgress(`Importing ${i + 1} of ${contracts.length}...`);
            imported.push(await send(contracts[i], false));
        }

        setResults(imported);
        setImporting(false);
    };

    //saves every file's errors to a text file
    const downloadErrorLog = () => {
        const lines: string[] = [];
        for (const result of results) {
            if (result.success) continue;

            lines.push(result.fileName + (result.contract_ID ? ` (${result.contract_ID})` : ''));
            if (result.message) lines.push('  ' + result.message);
            for (const problem of result.errors) lines.push('  - ' + problem);
            lines.push('');
        }

        const blob = new Blob([lines.join('\r\n')], {type: 'text/plain'});
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'Reins Import Errors.txt';
        link.click();

        window.URL.revokeObjectURL(url);
    };

    const reset = () => {
        setFiles([]);
        setContracts([]);
        setFileProblems([]);
        setResults([]);
        setCheckFailed(false);
        setError('');
    };

    const failed = results.filter(result => !result.success);
    const done = results.length > 0;

    let status = 'idle';

    if (error || failed.length > 0) {
        status = 'rejected';
    }
    else if (done) {
        status = 'done';
    }

    //return only has hover layout, default is below and displays conditionally based on state
    return (
        <div ref={drop} className='ClaimFormsDragAndDrop__root'>
            <Autocomplete
                className="dbContractSelectReins"
                size={"small"}
                disablePortal
                disableClearable
                options={GlobalConstants.VBA_DB}
                value={GlobalConstants.VBA_DB.find(option => option.id === currentDB) || null}
                sx={{ width: 150 }}
                renderInput={(params) => <TextField {...params}  />}
                onChange={onDBChange}
                disabled={importing}
            />

            {dragging && (
                <div ref={drag} className='ClaimFormsDragAndDrop__placeholder' style={{marginTop: '10vh'}}>
                    Drop That File And Watch It Go!
                    <span
                        role='img'
                        aria-label='emoji'
                        className='area__icon'
                    >
                        &#128565;
                    </span>
                </div>
            )}
            {!dragging ? <DefaultArea status={status}/> : null}

            {!dragging && (files.length > 0 || error) && (
                <div className='ClaimFormsDragAndDrop__summary'>
                    {contracts.length > 0 && !done && (
                        <div className='ClaimFormsDragAndDrop__note'>
                            {contracts.length} contract{contracts.length === 1 ? '' : 's'} found
                        </div>
                    )}

                    {fileProblems.length > 0 && !done && (
                        <div className='ClaimFormsDragAndDrop__error'>
                            {fileProblems.length} problem{fileProblems.length === 1 ? '' : 's'} with these files, so nothing will be imported.
                            Check them to get the full error log.
                        </div>
                    )}

                    {contracts.length > 0 && !done && (
                        <FormControlLabel
                            control={<Checkbox checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} disabled={importing}/>}
                            label="Update existing contracts"
                        />
                    )}

                    {(contracts.length > 0 || fileProblems.length > 0) && !done && (
                        <button
                            className='ClaimFormsDragAndDrop__button'
                            onClick={importAll}
                            disabled={importing}
                        >
                            {importing ? progress : `${fileProblems.length > 0 ? 'Check' : 'Import'} ${contracts.length} Contract${contracts.length === 1 ? '' : 's'}`}
                        </button>
                    )}

                    {importing && (
                        <div className='ClaimFormsDragAndDrop__note'>
                            Every contract is checked first. Nothing is written unless all of them pass.
                        </div>
                    )}

                    {done && !checkFailed && (
                        <div className='ClaimFormsDragAndDrop__count'>
                            {results.filter(result => result.success && !result.updated).length} imported,{' '}
                            {results.filter(result => result.success && result.updated).length} updated
                            {failed.length > 0 ? `, ${failed.length} failed` : ''}
                        </div>
                    )}

                    {/*a failed update can't be undone, so those contracts are changed even though they count as failed*/}
                    {done && !checkFailed && failed.some(result => result.updated) && (
                        <div className='ClaimFormsDragAndDrop__error'>
                            {failed.filter(result => result.updated).length} contract{failed.filter(result => result.updated).length === 1 ? ' was' : 's were'} only
                            partly updated, see the error log. Drop the same files again with Update existing contracts ticked to finish.
                        </div>
                    )}

                    {checkFailed && (
                        <div className='ClaimFormsDragAndDrop__error'>
                            Nothing was imported. {failed.length} of {results.length} {results.length === 1 ? 'has' : 'have'} problems.
                        </div>
                    )}

                    {failed.length > 0 && (
                        <button className='ClaimFormsDragAndDrop__button' onClick={downloadErrorLog}>
                            Download Error Log
                        </button>
                    )}

                    {error && <div className='ClaimFormsDragAndDrop__error'>{error}</div>}

                    {!importing && (
                        <button className='ClaimFormsDragAndDrop__link' onClick={reset}>
                            Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default React.memo(ReinsContractImporter);

//Regular no hover display
const DefaultArea = ({status}: {status: string}) => {
    if (status === 'rejected') {
        return (
            <div className='ClaimFormsDragAndDrop__area' style={{marginTop: '10vh'}}>
                Those Files Will Not Work
                <span
                    role='img'
                    aria-label='emoji'
                    className='area__icon'
                >
                    &#128533;
                </span>
            </div>
        )
    }

    if (status === 'done') {
        return (
            <div className='ClaimFormsDragAndDrop__area' style={{marginTop: '10vh'}}>
                Contracts Imported, Drag More Files?
                <span
                    role='img'
                    aria-label='emoji'
                    className='area__icon'
                >
                    &#128526;&#128077;
                </span>
            </div>
        )
    }

    return (
        <div className='ClaimFormsDragAndDrop__area' style={{marginTop: '10vh'}}>
            Drag And Drop The Reinsurance Contract Files
            <span
                role='img'
                aria-label='emoji'
                className='area__icon'
            >
                &#128526;
            </span>
        </div>
    )
}
